// =============================
// src/App.tsx
// =============================
import { useEffect } from "react";
import { motion } from "framer-motion";
import Header from "./components/Header";
import AOS from "aos";
import "aos/dist/aos.css";
import "./index.css";

export default function App() {
  useEffect(() => {
    AOS.init({ duration: 800, easing: "ease-in-out", once: true });
  }, []);

  return (
    <>
      <Header />
      <main className="text-white bg-black">
        {/* Hero Section */}
        <section
          className="flex flex-col items-center justify-center min-h-screen bg-cover bg-center text-white relative overflow-hidden px-6"
          style={{
            backgroundImage: `url(https://i.ibb.co/fdKrbPFg/ECIkigr-Ww-AAT3w.jpg)`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-black via-black/80 to-black/60 z-0" />

          <div className="z-10 flex flex-col items-center text-center">
            <a
              href="https://discord.gg/taZ6BJPYEv"
              target="_blank"
              rel="noopener noreferrer"
            >
              <motion.img
                src="https://i.ibb.co/2YChZYtV/Xd-Out-Roleplay-Transparent.webp"
                alt="X'd Out Roleplay Logo"
                className="w-48 md:w-64 mb-6 animate-pulse-glitch drop-shadow-xl"
                whileHover={{ scale: 1.05 }}
                transition={{ type: "spring", stiffness: 200 }}
              />
            </a>

            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mb-3"
            >
              <span className="inline-flex items-center gap-2 text-xs md:text-sm px-3 py-1 rounded-full bg-green-600/20 text-green-300 border border-green-600/40">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                Now Live — Join the City
              </span>
            </motion.div>

            <motion.h1
              className="text-4xl md:text-6xl font-extrabold relative glitch-horizontal mb-2"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <span className="relative z-10">X’d Out Roleplay</span>
              <span
                aria-hidden="true"
                className="absolute inset-0 glitch-layer glitch-left"
              >
                X’d Out Roleplay
              </span>
              <span
                aria-hidden="true"
                className="absolute inset-0 glitch-layer glitch-right"
              >
                X’d Out Roleplay
              </span>
            </motion.h1>

            <motion.p
              className="mt-2 text-lg md:text-xl text-purple-300 font-light italic tracking-wide z-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              You’ve been erased. Now write your own story.
            </motion.p>

            <motion.p
              className="mt-4 text-lg text-gray-300 max-w-xl z-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
            >
              Live, story-first GTA RP for 18+ players. Balanced economy, strict
              anti-toxicity, and long-term character growth.
            </motion.p>

            <motion.div
              className="flex flex-wrap justify-center gap-4 mt-8 z-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2 }}
            >
              <a
                href="https://discord.gg/taZ6BJPYEv"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-2 bg-purple-600 hover:bg-purple-700 transition rounded-xl shadow-lg text-white font-semibold"
              >
                Join Discord
              </a>
              <a
                href="https://forum.cfx.re/t/x-d-out-roleplay-18-immersive-rp-build-your-legacy/5356169"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 transition rounded-xl shadow-lg text-white font-semibold"
              >
                Visit Forums
              </a>
              <a
                href="/rules"
                className="px-6 py-2 bg-zinc-800 hover:bg-zinc-700 transition rounded-xl shadow-lg text-white font-semibold"
              >
                Read the Rules
              </a>
            </motion.div>

            <motion.p
              className="mt-10 text-sm text-gray-500 z-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5 }}
            >
              © 2025 X'd Out Roleplay. All rights reserved.
            </motion.p>
          </div>
        </section>

        {/* About Section */}
        <section
          className="py-24 px-6 bg-[#111] text-center"
          data-aos="fade-up"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            What is X'd Out?
          </h2>
          <p className="text-gray-300 max-w-3xl mx-auto">
            X'd Out Roleplay is a modern 18+ GTA RP server focused on character
            depth, real-world consequences, and long-form storytelling. We’re
            building a space where your choices matter — and your story can
            truly unfold.
          </p>
        </section>

        {/* Features Section */}
        <section
          className="py-24 px-6 bg-gradient-to-b from-[#111] to-black text-center"
          data-aos="fade-up"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Key Features</h2>
          <div className="grid gap-8 md:grid-cols-3 max-w-6xl mx-auto text-left mt-10">
            <div
              data-aos="fade-up"
              className="bg-black/40 p-6 rounded-lg border border-gray-700"
            >
              <h3 className="text-xl font-semibold mb-2 text-purple-400">
                Immersive Roleplay
              </h3>
              <p className="text-gray-300">
                Designed for realism, story depth, and progression — no
                fast-tracks, just meaningful growth.
              </p>
            </div>
            <div
              data-aos="fade-up"
              data-aos-delay="100"
              className="bg-black/40 p-6 rounded-lg border border-gray-700"
            >
              <h3 className="text-xl font-semibold mb-2 text-purple-400">
                Balanced Economy
              </h3>
              <p className="text-gray-300">
                Every dollar counts. Build a living, start a business, or run
                the streets — the choice is yours.
              </p>
            </div>
            <div
              data-aos="fade-up"
              data-aos-delay="200"
              className="bg-black/40 p-6 rounded-lg border border-gray-700"
            >
              <h3 className="text-xl font-semibold mb-2 text-purple-400">
                Toxic-Free Zone
              </h3>
              <p className="text-gray-300">
                Curated for maturity, story-first RP, and staff who care. No
                spam wars, just immersive narrative.
              </p>
            </div>
          </div>
        </section>

        {/* Philosophy Section */}
        <section className="py-20 px-6 bg-black text-center" data-aos="fade-up">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Why We’re Different
          </h2>
          <p className="text-gray-300 max-w-3xl mx-auto">
            X’d Out Roleplay isn’t just another server — it’s a reset. Built for
            mature players who want long-term progression, real choices, and
            immersive stories. No spam wars. No ego-driven chaos. Just clean,
            gritty roleplay in a world that reacts to you.
          </p>
        </section>

        {/* CTA Section */}
        <section
          className="py-20 px-6 bg-[#111] text-center"
          data-aos="fade-up"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to jump in?
          </h2>
          <p className="text-gray-300 max-w-2xl mx-auto">
            Join the Discord to get whitelisted roles, read the latest
            changelog, and meet the community.
          </p>
          <div className="mt-6 flex flex-wrap gap-4 justify-center">
            <a
              href="https://discord.gg/taZ6BJPYEv"
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-3 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-semibold shadow"
            >
              Join the Discord
            </a>
            <a
              href="/rules"
              className="px-6 py-3 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-white font-semibold shadow"
            >
              View Rules
            </a>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-10 text-center bg-black text-gray-500 text-sm space-y-2">
          <p>
            © 2025 X'd Out Roleplay · Made with passion for immersive stories
          </p>
          <p>
            Created with ❤️ by{" "}
            <span className="text-purple-300 font-semibold">Vinny X</span>
          </p>
        </footer>
      </main>
    </>
  );
}